import { postTypeIconClassName } from "@/features/post/post-type-icon";
import React from "react";
import LegacyTooltip from "../../components/CustomOverlayTrigger/LegacyTooltip";
import OverlayTrigger from "../../components/CustomOverlayTrigger/CustomOverlayTrigger";

import { Icon } from "@dialogueconsulting/sked-ui";

import UserIcon from "assets/icons/user.svg";
import TrashIcon from "assets/icons/trash.svg";
import PencilIcon from "assets/icons/pencil.svg";
import RetryIcon from "assets/icons/retry.svg";
import IgStoryGradientIcon from "assets/icons/ig-story-grad.svg";
import LibStorage from "assets/icons/lib-storage.svg";
import ChromeIcon from "assets/icons/chrome.svg";
import StockIcon from "assets/icons/stock.svg";
import LightningIcon from "assets/icons/lightning.svg";
import SendPlane from "assets/icons/send-plane.svg";
import CopyIcon from "assets/icons/copy.svg";
import BusinessIcon from "assets/icons/business.svg";
import BurgerIcon from "assets/icons/category-list-view.svg";
import DraftIcon from "assets/icons/calendar/draft.svg";

import CrossIcon from "assets/icons/cross.svg";
import InfoIcon from "assets/icons/info.svg";
import InfoLightIcon from "assets/icons/info-light.svg";
import WarningIcon from "assets/icons/warning.svg";
import SuccessIcon from "assets/icons/success.svg";
import ExclamRoundIcon from "assets/icons/exclam-round.svg";
import HorizontalDividerCheckIcon from "assets/icons/horizontal-divider-check.svg";

import ItemSave from "assets/icons/item-save.svg";
import ItemSaveWhiteOutline from "assets/icons/item-save-white-outline.svg";
import ItemEdit from "assets/icons/item-edit.svg";
import ItemRemove from "assets/icons/item-remove.svg";
import TickCircleOutline from "assets/icons/tick-circle-outline.svg";
import RightToBracket from "assets/icons/right-to-bracket.svg";
import XCircle from "assets/icons/x-circle.svg";

import "./SkedIcon.less";

export interface SkedIconProps {
  icon: string;
  size?: string | number;
  color?: string;
  className?: string;
  placement?: "top" | "bottom" | "left" | "right";
  tooltip?: string;
  onClick?(event: React.MouseEvent<HTMLSpanElement>): void;
  disabled?: boolean;
  style?: React.CSSProperties;
  ariaLabel?: string;
  testId?: string;
}

const SkedIcon = (props: SkedIconProps) => {
  const {
    icon,
    size,
    color,
    className,
    tooltip,
    placement = "top",
    onClick,
    disabled = false,
    style = {},
    ariaLabel,
  } = props;

  const iconElement = getIconElement(icon);

  let modifiers = className === undefined ? "" : className;
  if (!size) {
    modifiers += ` SkedIcon_size_14`;
  }

  if (size) {
    modifiers += ` SkedIcon_size_${size}`;
  }

  if (color && !disabled) {
    modifiers += ` SkedIcon_color_${color}`;
  }

  if (disabled) {
    modifiers += ` SkedIcon_color_gray`;
  }

  // need to figure out a better way to do this.
  if (icon !== "igStoryGradient") {
    modifiers += " SkedIcon_color_default";
  }
  const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (disabled) {
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  let iconJsx = (
    <span
      onClick={handleClick}
      className={`SkedIcon icon ${icon} ${modifiers}`}
      aria-label={ariaLabel}
      {...(props.testId ? { "data-testid": props.testId } : {})}
    />
  );

  if (iconElement) {
    iconJsx = (
      <span
        style={style}
        onClick={handleClick}
        className={`SkedIcon icon ${modifiers}`}
        aria-label={ariaLabel}
        {...(props.testId ? { "data-testid": props.testId } : {})}
      >
        {iconElement}
      </span>
    );
  }

  if (tooltip && placement) {
    const tooltipJsx = <LegacyTooltip id={icon}>{tooltip}</LegacyTooltip>;

    return (
      <OverlayTrigger placement={placement} overlay={tooltipJsx}>
        {iconJsx}
      </OverlayTrigger>
    );
  }
  return iconJsx;
};

const legacyFontAwesomeIcons: Record<string, string> = {
  "icon-archive-inbox": "box-archive",
  "icon-assign-to": "user-plus",
  "icon-category-list-view": "list",
  "icon-close": "x-close",
  "icon-crop-2": "link-external-01",
  "icon-down-close": "chevron-down",
  "icon-eye": "eye",
  "icon-has-note": "note-sticky",
  "icon-hashtag-sr": "hashtag",
  "icon-help": "help-circle",
  "icon-left-close": "chevron-left",
  "icon-left-move-collection": "folder-arrow-left",
  "icon-link": "link",
  "icon-location": "location-dot",
  "icon-mentioned": "at",
  "icon-neutral": "circle",
  "icon-pencil": "pen",
  "icon-queue": "list-check",
  "icon-refresh": "rotate-right",
  "icon-retry": "copy",
  "icon-search": "search",
  "icon-send-draft": "paper-plane",
  "icon-shopping-bag": "bag-shopping",
  "icon-star-empty": "star",
  "icon-star-full": "fa-solid fa-star",
  "icon-tag": "tag",
  "icon-up-close": "chevron-up",
  "icon-upcoming": "clock",
};

const postIconTypes: Record<string, string> = {
  "icon-post-carousel": "multiMedia",
  "icon-post-image": "image",
  "icon-post-text": "text",
  "icon-post-document": "document",
  "icon-post-reel": "reel",
  "icon-post-video": "video",
  "icon-post-story": "story",
  "icon-post-igtv": "video",
  igStory: "story",
  igReel: "reel",
  igtv: "video",
  video: "video",
};

const getIconElement = (icon: string) => {
  if (typeof icon !== "string" || !icon) return null;
  if (postIconTypes[icon]) {
    return (
      <i
        className={postTypeIconClassName({ type: postIconTypes[icon] })}
        aria-hidden="true"
      />
    );
  }
  switch (icon) {
    case "user": {
      return <UserIcon />;
    }
    case "draft": {
      return <DraftIcon />;
    }
    case "trash": {
      return <TrashIcon />;
    }
    case "pencil": {
      return <PencilIcon />;
    }
    case "retry": {
      return <RetryIcon />;
    }
    case "igStoryGradient": {
      return <IgStoryGradientIcon />;
    }
    case "chromeIcon": {
      return <ChromeIcon />;
    }
    case "stockIcon": {
      return <StockIcon />;
    }
    case "libStorage": {
      return <LibStorage />;
    }
    case "chevronLeft": {
      return <i className="fa-solid fa-chevron-left" />;
    }
    case "chevronRight": {
      return <i className="fa-solid fa-chevron-right" />;
    }
    case "chevronDown": {
      return <i className="fa-solid fa-chevron-down" />;
    }
    case "chevronUp": {
      return <i className="fa-solid fa-chevron-up" />;
    }
    case "lightning": {
      return <LightningIcon />;
    }
    case "send-plane": {
      return <SendPlane />;
    }
    case "copy": {
      return <CopyIcon />;
    }
    case "warning": {
      return <WarningIcon />;
    }
    case "warningNew": {
      return <WarningIcon />;
    }
    case "success": {
      return <SuccessIcon />;
    }
    case "info": {
      return <InfoIcon />;
    }
    case "info-light": {
      return <InfoLightIcon />;
    }
    case "error": {
      return <CrossIcon />;
    }
    case "business": {
      return <BusinessIcon />;
    }
    case "burger": {
      return <BurgerIcon />;
    }
    case "exclam-round": {
      return <ExclamRoundIcon />;
    }
    case "horizontal-check": {
      return <HorizontalDividerCheckIcon />;
    }
    case "check": {
      return <i className="fa-solid fa-check" />;
    }
    case "close": {
      return <i className="fa-solid fa-xmark" />;
    }
    case "item-save": {
      return <ItemSave />;
    }
    case "item-save-white-outline": {
      return <ItemSaveWhiteOutline />;
    }
    case "item-edit": {
      return <ItemEdit />;
    }
    case "item-remove": {
      return <ItemRemove />;
    }
    case "tick-circle-outline": {
      return <TickCircleOutline />;
    }
    case "right-to-bracket": {
      return <RightToBracket />;
    }
    case "x-circle": {
      return <XCircle />;
    }
    default: {
      const fontAwesomeIcon = legacyFontAwesomeIcons[icon];
      if (fontAwesomeIcon) {
        if (fontAwesomeIcon.startsWith("fa-")) {
          return <i className={fontAwesomeIcon} />;
        }
        return <Icon name={fontAwesomeIcon} />;
      }

      if (icon.startsWith("fa-")) {
        return <i className={icon} />;
      }

      return <Icon name={icon.replace(/^icon-/, "")} />;
    }
  }
};

export default SkedIcon;
